/**
 * Where a "Log in" link or a redirect of a logged-out user goes, the same in every app:
 *
 * - DEV / TEST / PROD: SSO's login, `{ssoUrl}/?flow=SelfService&continue=…`.
 * - localhost (`next dev`): the local login page, `/login?continue=…` (`docs/auth.md`, "Local login"),
 *   or `localLoginUrl` for an app without its own (the brackets apps use pickleball's).
 *
 * `isLocalhost` is the app's `IS_LOCALHOST` (its own URL is on localhost), not `NODE_ENV`, which
 * isn't available in the browser. Apps call it through their own `getLoginUrl(continueTo)`, which
 * passes their env.
 */

export interface LoginUrlOptions {
  /** `publicEnv.NEXT_PUBLIC_SSO_URL`. */
  ssoUrl: string;
  /** The app's `IS_LOCALHOST`. */
  isLocalhost: boolean;
  /**
   * Where the user comes back after logging in. SSO needs an absolute URL; the local `/login` also
   * takes a path. Left out: SSO's default page, or `/` locally.
   */
  continueTo?: string | URL;
  /** The local login page; default `/login`, this app's. */
  localLoginUrl?: string;
}

export const buildLoginUrl = ({
  ssoUrl,
  isLocalhost,
  continueTo,
  localLoginUrl = '/login',
}: LoginUrlOptions): string => {
  const continueValue = continueTo?.toString();

  if (isLocalhost) {
    return continueValue
      ? `${localLoginUrl}?${new URLSearchParams({ continue: continueValue }).toString()}`
      : localLoginUrl;
  }

  const params = new URLSearchParams({ flow: 'SelfService' });

  if (continueValue) {
    params.set('continue', continueValue);
  }

  return `${ssoUrl}/?${params.toString()}`;
};
